/**
 * BIP-39 mnemonic generation/validation and BIP-85 deterministic key derivation.
 *
 * This module is the cryptographic foundation of Satsflow Wallet. The master mnemonic
 * is the root of all identity and storage:
 * - Index 0: Evolu child mnemonic (local-first database encryption key)
 * - Index 1: Nostr child mnemonic (messaging and contact identity via NIP-06)
 *
 * SECURITY:
 * - Functions are pure (no side effects, no storage, no logging).
 * - Never log mnemonics, seeds, or private keys.
 * - All crypto operations use audited libraries (@scure/bip39, @scure/bip32, @noble/hashes).
 *
 * @module crypto
 */

import { HDKey } from '@scure/bip32';
import {
  generateMnemonic as bip39Generate,
  mnemonicToSeedSync,
  validateMnemonic as bip39Validate,
  entropyToMnemonic,
} from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { hmac } from '@noble/hashes/hmac';
import { sha512 } from '@noble/hashes/sha512';

/**
 * BIP-85 derivation path template for generating child mnemonics.
 * m/83696968' (BIP-85 purpose) / 39' (BIP-39 app) / 0' (English) / 12' (12 words) / INDEX'
 */
const BIP85_PATH_PREFIX = "m/83696968'/39'/0'/12'";

/**
 * HMAC key used in BIP-85 to derive entropy from a child private key.
 * This is the exact string specified in the BIP-85 standard.
 */
const BIP85_HMAC_KEY = 'bip-entropy-from-k';

/**
 * NIP-06 derivation path for Nostr keys from a BIP-39 mnemonic.
 * m/44'/1237'/0'/0/0
 */
const NIP06_PATH = "m/44'/1237'/0'/0/0";

/**
 * Generate a new 12-word BIP-39 mnemonic using cryptographically secure randomness.
 * Uses the English wordlist.
 *
 * @returns A space-separated 12-word mnemonic string
 */
export function generateMnemonic(): string {
  return bip39Generate(wordlist, 128);
}

/**
 * Validate a mnemonic string against the BIP-39 specification.
 * Checks word count, wordlist membership, and checksum.
 *
 * @param mnemonic - Space-separated mnemonic words to validate
 * @returns true if the mnemonic is valid BIP-39, false otherwise
 */
export function validateMnemonic(mnemonic: string): boolean {
  if (typeof mnemonic !== 'string' || mnemonic.trim() === '') {
    return false;
  }
  return bip39Validate(mnemonic, wordlist);
}

/**
 * Derive a child BIP-39 mnemonic from a master mnemonic using BIP-85.
 *
 * Algorithm (per BIP-85 specification):
 * 1. Convert master mnemonic to BIP-39 seed
 * 2. Create HD master key from seed
 * 3. Derive child key at path: m/83696968'/39'/0'/12'/INDEX'
 * 4. Compute HMAC-SHA512 of child private key with key "bip-entropy-from-k"
 * 5. Take first 16 bytes (128 bits) for 12-word mnemonic
 * 6. Convert entropy to BIP-39 mnemonic
 *
 * Index assignments (per ADR-014):
 * - Index 0: Evolu database child mnemonic
 * - Index 1: Nostr identity child mnemonic
 *
 * @param masterMnemonic - Valid BIP-39 master mnemonic
 * @param index - BIP-85 child index (non-negative integer, max 2^31 - 1)
 * @returns Derived 12-word BIP-39 child mnemonic
 * @throws Error if master mnemonic is invalid or index is out of range
 */
export function deriveChildMnemonic(masterMnemonic: string, index: number): string {
  if (typeof masterMnemonic !== 'string' || masterMnemonic.trim() === '') {
    throw new Error('Master mnemonic must be a non-empty string');
  }

  if (!bip39Validate(masterMnemonic, wordlist)) {
    throw new Error('Invalid master mnemonic');
  }

  if (!Number.isInteger(index) || index < 0) {
    throw new Error('Index must be a non-negative integer');
  }

  if (index > 0x7fffffff) {
    throw new Error('Index exceeds maximum hardened derivation limit (2^31 - 1)');
  }

  const seed = mnemonicToSeedSync(masterMnemonic);
  const masterKey = HDKey.fromMasterSeed(seed);
  const childPath = `${BIP85_PATH_PREFIX}/${index}'`;
  const child = masterKey.derive(childPath);

  if (!child.privateKey) {
    throw new Error('Failed to derive child private key');
  }

  // BIP-85: HMAC-SHA512 with key "bip-entropy-from-k"
  const entropy = hmac(sha512, BIP85_HMAC_KEY, child.privateKey);

  // Take first 16 bytes (128 bits) for 12-word mnemonic
  return entropyToMnemonic(entropy.slice(0, 16), wordlist);
}

/**
 * Nostr keypair derived from BIP-85 child mnemonic at index 1, using NIP-06.
 */
export interface NostrKeypair {
  /** Raw 32-byte private key */
  readonly privateKey: Uint8Array;
  /** Hex-encoded 32-byte public key */
  readonly publicKey: string;
}

/**
 * Derive a Nostr keypair from a master mnemonic.
 *
 * Process:
 * 1. Derive child mnemonic at BIP-85 index 1
 * 2. Convert child mnemonic to BIP-39 seed
 * 3. Derive key at NIP-06 path: m/44'/1237'/0'/0/0
 * 4. Return private key bytes and hex-encoded public key
 *
 * @param masterMnemonic - Valid BIP-39 master mnemonic
 * @returns Nostr keypair with private key bytes and hex public key
 * @throws Error if master mnemonic is invalid or key derivation fails
 */
export function deriveNostrKeypair(masterMnemonic: string): NostrKeypair {
  const childMnemonic = deriveChildMnemonic(masterMnemonic, 1);
  const childSeed = mnemonicToSeedSync(childMnemonic);
  const hdKey = HDKey.fromMasterSeed(childSeed);
  const nostrKey = hdKey.derive(NIP06_PATH);

  if (!nostrKey.privateKey) {
    throw new Error('Failed to derive Nostr private key');
  }

  if (!nostrKey.publicKey) {
    throw new Error('Failed to derive Nostr public key');
  }

  // Public key is the x-coordinate of the compressed key (remove 0x02/0x03 prefix)
  const pubkeyHex = bytesToHex(nostrKey.publicKey.slice(1));

  return {
    privateKey: new Uint8Array(nostrKey.privateKey),
    publicKey: pubkeyHex,
  };
}

/**
 * Derive a BIP-39 seed for Cashu NUT-13 deterministic secrets.
 *
 * Derivation chain:
 * 1. Master mnemonic -> BIP-85 child mnemonic at index 0
 * 2. Child mnemonic -> BIP-39 seed bytes (64 bytes)
 *
 * The resulting seed is passed to cashu-ts Wallet as `bip39seed` to enable
 * deterministic token secrets that can be recovered via NUT-09 restore.
 *
 * @param masterMnemonic - Valid BIP-39 master mnemonic
 * @returns 64-byte Uint8Array seed for cashu-ts
 * @throws Error if master mnemonic is invalid
 */
export function deriveCashuSeed(masterMnemonic: string): Uint8Array {
  if (typeof masterMnemonic !== 'string' || masterMnemonic.trim() === '') {
    throw new Error('Master mnemonic must be a non-empty string');
  }

  if (!bip39Validate(masterMnemonic, wordlist)) {
    throw new Error('Invalid master mnemonic');
  }

  const childMnemonic = deriveChildMnemonic(masterMnemonic, 0);
  return mnemonicToSeedSync(childMnemonic);
}

/**
 * Convert a byte array to a hex string.
 *
 * @param bytes - Byte array to convert
 * @returns Lowercase hex string
 */
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
